/**
 * Life4Earth V2 — User Resource Registry API Adapter
 *
 * Endpoint: /api/UserResources
 *
 * Provides typed fetch functions and shape converters for user-registered
 * resources (the supply side of the matching engine).
 */

import { apiFetch } from './client';
import { normalizeGuid } from '../utils/guid';
import type { UserResource, ResourcePhoto } from '../Data/UserResources/userResourceTypes';

// ── API Response Types ──────────────────────────────────────

export interface UserResourceResponse {
  id: string;
  userId: string;
  category: string;
  label: string;
  description: string | null;
  state: string;
  offerType: string;
  sharedCommunities: string[];
  photos: string;                      // JSONB string from backend
  emergencyAvailable: boolean;
  emergencyOnly: boolean;
  deploymentTimeMinutes: number | null;
  deploymentTimeUnit: string | null;
  emergencyNotes: string | null;
  certificationNumber: string | null;
  certificationDate: string | null;
  certificationExpiry: string | null;
  conditions: string | null;
  committedProjectGuid: string | null;

  // Offer handshake fields (migration 112)
  offeredAt: string | null;
  offerExpiresAt: string | null;
  offeredItemGuid: string | null;
  offerNotes: string | null;
  reviewedByGuid: string | null;
  reviewedAt: string | null;
  reviewDecision: string | null;
  reviewNote: string | null;

  sortOrder: number;
  createdAt: string;
  updatedAt: string;

  // Legacy fields (still returned by backend for backward compat)
  descriptorTags: string[];
  availabilityType: string;
  reach: string;
  locationEntityGuid: string | null;
  quantity: number | null;
  unit: string | null;
  visibility: string;
}

// ── Shape Converters ────────────────────────────────────────

function parsePhotos(raw: string | null | undefined): ResourcePhoto[] {
  if (!raw) return [];
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(arr)) return [];
    return arr.map((p: any, i: number) => ({
      url: String(p.url || ''),
      sort_order: typeof p.sort_order === 'number' ? p.sort_order : i,
    })).filter((p: ResourcePhoto) => p.url);
  } catch {
    return [];
  }
}

export function apiResponseToUserResource(r: UserResourceResponse): UserResource {
  return {
    resource_guid: normalizeGuid(r.id),
    user_guid: normalizeGuid(r.userId),
    category: r.category as UserResource['category'],
    label: r.label,
    description: r.description ?? undefined,
    state: (r.state || 'available') as UserResource['state'],
    offer_type: (r.offerType || 'loan') as UserResource['offer_type'],
    shared_communities: (r.sharedCommunities || []).map(normalizeGuid),
    photos: parsePhotos(r.photos),
    emergency_available: r.emergencyAvailable ?? false,
    emergency_only: r.emergencyOnly ?? false,
    deployment_time_minutes: r.deploymentTimeMinutes,
    deployment_time_unit: r.deploymentTimeUnit || 'minutes',
    emergency_notes: r.emergencyNotes,
    certification_number: r.certificationNumber || null,
    certification_date: r.certificationDate || null,
    certification_expiry: r.certificationExpiry || null,
    conditions: r.conditions,
    committed_project_guid: r.committedProjectGuid ? normalizeGuid(r.committedProjectGuid) : null,

    // Offer handshake
    offered_at: r.offeredAt || null,
    offer_expires_at: r.offerExpiresAt || null,
    offered_item_guid: r.offeredItemGuid ? normalizeGuid(r.offeredItemGuid) : null,
    offer_notes: r.offerNotes || null,
    reviewed_by_guid: r.reviewedByGuid ? normalizeGuid(r.reviewedByGuid) : null,
    reviewed_at: r.reviewedAt || null,
    review_decision: (r.reviewDecision as UserResource['review_decision']) || null,
    review_note: r.reviewNote || null,

    sort_order: r.sortOrder,
    created_at: r.createdAt,
    updated_at: r.updatedAt,

    // Legacy fields (passthrough)
    descriptor_tags: r.descriptorTags || [],
    availability_type: r.availabilityType || 'ongoing',
    reach: r.reach || 'local',
    visibility: r.visibility || 'community',
    location_entity_guid: r.locationEntityGuid ? normalizeGuid(r.locationEntityGuid) : null,
    quantity: r.quantity,
    unit: r.unit,
  };
}

export function userResourceToApiPayload(item: UserResource): Record<string, unknown> {
  return {
    id: item.resource_guid,
    userId: item.user_guid,
    category: item.category,
    label: item.label,
    description: item.description || null,
    state: item.state,
    offerType: item.offer_type,
    sharedCommunities: item.shared_communities,
    photos: JSON.stringify(item.photos || []),
    emergencyAvailable: item.emergency_available ?? false,
    emergencyOnly: item.emergency_only ?? false,
    deploymentTimeMinutes: item.deployment_time_minutes ?? null,
    deploymentTimeUnit: item.deployment_time_unit || 'minutes',
    emergencyNotes: item.emergency_notes || null,
    certificationNumber: item.certification_number || null,
    certificationDate: item.certification_date || null,
    certificationExpiry: item.certification_expiry || null,
    conditions: item.conditions || null,
    committedProjectGuid: item.committed_project_guid || null,

    // Offer handshake
    offeredItemGuid: item.offered_item_guid || null,
    offerNotes: item.offer_notes || null,

    sortOrder: item.sort_order,

    // Legacy fields (send defaults so backend doesn't reject)
    descriptorTags: item.descriptor_tags || [],
    availabilityType: item.availability_type || 'ongoing',
    reach: item.reach || 'local',
    locationEntityGuid: item.location_entity_guid || null,
    quantity: item.quantity ?? null,
    unit: item.unit || null,
    visibility: item.visibility || 'community',
  };
}

// ── API Functions ───────────────────────────────────────────

/**
 * Fetch all resources registered by a specific user.
 */
export async function fetchUserResources(userGuid: string): Promise<UserResource[]> {
  const resp = await apiFetch<UserResourceResponse[]>(
    `/api/UserResources?userId=${encodeURIComponent(userGuid)}`
  );
  return resp.map(apiResponseToUserResource);
}

/**
 * Create or update a user resource.
 * Backend upserts based on the resource_guid.
 */
export async function saveUserResource(item: UserResource): Promise<UserResource> {
  const resp = await apiFetch<UserResourceResponse>('/api/UserResources', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userResourceToApiPayload(item)),
  });
  return apiResponseToUserResource(resp);
}

/**
 * Delete a user resource permanently.
 */
export async function deleteUserResource(resourceGuid: string): Promise<void> {
  await apiFetch(`/api/UserResources/${encodeURIComponent(resourceGuid)}`, {
    method: 'DELETE',
  });
}

/**
 * Batch save user resources (for reordering, bulk edits).
 */
export async function saveUserResourcesBatch(items: UserResource[]): Promise<UserResource[]> {
  const resp = await apiFetch<UserResourceResponse[]>('/api/UserResources/batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(items.map(userResourceToApiPayload)),
  });
  return resp.map(apiResponseToUserResource);
}

// ── Offer Handshake API ────────────────────────────────────

export interface OfferResourcePayload {
  offeredItemGuid?: string;
  projectGuid?: string;
  offerNotes?: string;
  expirationDays?: 7 | 14 | 30;
}

/**
 * Offer a resource to a project need. Transitions state to 'reserved'.
 */
export async function offerResource(
  resourceGuid: string,
  payload: OfferResourcePayload
): Promise<UserResource> {
  const resp = await apiFetch<UserResourceResponse>(
    `/api/UserResources/${encodeURIComponent(resourceGuid)}/offer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return apiResponseToUserResource(resp);
}

export interface ReviewOfferPayload {
  decision: 'accepted' | 'rejected';
  reviewNote?: string;
}

/**
 * Accept or reject a pending resource offer.
 */
export async function reviewResourceOffer(
  resourceGuid: string,
  payload: ReviewOfferPayload
): Promise<UserResource> {
  const resp = await apiFetch<UserResourceResponse>(
    `/api/UserResources/${encodeURIComponent(resourceGuid)}/review`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return apiResponseToUserResource(resp);
}

/**
 * Fetch all pending resource offers for a project.
 * Used by Contributions tab and Action Center.
 */
export async function fetchPendingOffers(projectId?: string): Promise<UserResource[]> {
  const url = projectId
    ? `/api/UserResources/pending-offers?projectId=${encodeURIComponent(projectId)}`
    : '/api/UserResources/pending-offers';
  const resp = await apiFetch<UserResourceResponse[]>(url);
  return resp.map(apiResponseToUserResource);
}
